import jsPDF from "jspdf";
import { Localization } from "@viamap/viamap2-common";
import { SERVITUT_ANALYSIS_FIELDS, analysisFieldLabel } from "src/modules/ANNModule/ANNServitutFieldAnnotations";
import type { ServitutMatrixColumnDetail, ServitutExportProgress } from "../model/report";
import {
   selectFieldText,
   selectIdentityLabel,
   selectIsApproved,
   selectIsRejected,
} from "../store/selectors";
import type { ServitutRow } from "../model/row";

const COLORS = {
   headerBg: [238, 244, 242] as const,
   headerText: [41, 75, 70] as const,
   text: [38, 59, 56] as const,
   muted: [89, 113, 109] as const,
   border: [197, 208, 205] as const,
   row: [255, 255, 255] as const,
   approved: [216, 245, 223] as const,
   rejected: [253, 232, 232] as const,
   accentApproved: [47, 158, 68] as const,
   accentRejected: [201, 42, 42] as const,
   includedBg: [229, 246, 233] as const,
   includedText: [31, 107, 58] as const,
};

const MARGIN = 10;
const FOOTER_H = 10;
const MIN_ROW_H = 11;
const BODY_LINE_H = 3.4;
const LABEL_LINE_H = 4.1;
const CELL_PAD = 1.6;
const PRIORITY_W = 18;
const STATUS_W = 22;
const MIN_IDENTITY_W = 32;
const MAX_IDENTITY_W = 52;
const MIN_FIELDS_W = 92;
const MIN_BFE_W = 5.2;
const MAX_BFE_W = 8;

export type ServitutPdfField = {
   key: (typeof SERVITUT_ANALYSIS_FIELDS)[number];
   label: string;
   text: string;
};

export type ServitutPdfRowModel = {
   id: string;
   priority: string;
   identity: string;
   heading: string;
   status: string;
   statusTone: "approved" | "rejected" | "pending";
   fields: ServitutPdfField[];
   includedBfes: number[];
};

export type ServitutPdfColumnModel = {
   bfe: number;
   label: string;
};

export type ServitutPdfModel = {
   title: string;
   generatedAt: string;
   generatedStamp: string;
   caseDescription: string;
   easementCountLabel: string;
   fieldHeader: string;
   columns: ServitutPdfColumnModel[];
   rows: ServitutPdfRowModel[];
};

export function bfeHeaderShortLabel(
   bfe: number,
   columnLabels: Record<string, string> = {},
   columnDetails: Record<string, ServitutMatrixColumnDetail> = {},
): string {
   const detail = columnDetails[String(bfe)];
   if (detail?.matrikler?.length) {
      return `${detail.matrikler[0]}${detail.matrikler.length > 1 ? " m.fl." : ""}`;
   }
   const label = columnLabels[String(bfe)] || columnLabels[bfe as unknown as string] || `${bfe}`;
   return label.split(", ")[0] || `${bfe}`;
}

function displayOrDash(value: string): string {
   return value.trim() || "–";
}

/** Short tingbog title under dato-løbenr. The analysis resumé belongs in the Resumé field only. */
function pdfDatoLoebenrHeading(row: ServitutRow, resumeText: string): string {
   const heading = (row.landRegistry.summary || "").trim();
   if (!heading || heading === resumeText.trim()) {
      return "";
   }
   return heading;
}

function statusLabel(row: ServitutRow): { text: string; tone: ServitutPdfRowModel["statusTone"] } {
   if (selectIsRejected(row)) {
      return { text: Localization.getText("Ignored"), tone: "rejected" };
   }
   if (selectIsApproved(row)) {
      return { text: Localization.getText("Approved"), tone: "approved" };
   }
   return { text: Localization.getText("Not approved yet"), tone: "pending" };
}

export function buildServitutPdfModel(
   rows: ServitutRow[],
   columns: number[],
   columnLabels: Record<string, string> = {},
   options?: { caseDescription?: string; columnDetails?: Record<string, ServitutMatrixColumnDetail> },
): ServitutPdfModel {
   const caseDescription = (options?.caseDescription || "").trim();
   const columnDetails = options?.columnDetails || {};
   const fields = SERVITUT_ANALYSIS_FIELDS.map((key) => ({
      key,
      label: analysisFieldLabel(key),
   }));
   return {
      title: Localization.getText("Easement review"),
      generatedAt: new Date().toLocaleString("da-DK"),
      generatedStamp: Localization.getText("Generated by ServiMap"),
      caseDescription,
      easementCountLabel: `${rows.length} ${Localization.getText("Easements")}`,
      fieldHeader: fields.map((field) => field.label).join("  ·  "),
      columns: columns.map((bfe) => ({
         bfe,
         label: bfeHeaderShortLabel(bfe, columnLabels, columnDetails),
      })),
      rows: rows.map((row) => {
         const status = statusLabel(row);
         const resumeText = selectFieldText(row, "resume", caseDescription);
         return {
            id: row.id,
            priority: `${row.landRegistry.priority || ""}`.trim() || "–",
            identity: selectIdentityLabel(row) || row.landRegistry.displayAktId || row.id,
            heading: pdfDatoLoebenrHeading(row, resumeText),
            status: status.text,
            statusTone: status.tone,
            fields: fields.map((field) => ({
               ...field,
               text: displayOrDash(selectFieldText(row, field.key, caseDescription)),
            })),
            includedBfes: [...row.landRegistry.includedBfes],
         };
      }),
   };
}

type TableLayout = {
   priorityW: number;
   identityW: number;
   statusW: number;
   fieldsW: number;
   bfeW: number;
   showFields: boolean;
};

function tableInnerWidth(pageWidth: number): number {
   return pageWidth - MARGIN * 2;
}

function maxBfeColumns(pageWidth: number): number {
   const leftover = tableInnerWidth(pageWidth) - PRIORITY_W - STATUS_W - MIN_IDENTITY_W - MIN_FIELDS_W;
   return Math.max(1, Math.floor(leftover / MIN_BFE_W));
}

export function chunkPdfBfeColumns(columns: ServitutPdfColumnModel[], pageWidth: number): ServitutPdfColumnModel[][] {
   if (!columns.length) {
      return [[]];
   }
   const size = maxBfeColumns(pageWidth);
   const chunks: ServitutPdfColumnModel[][] = [];
   for (let i = 0; i < columns.length; i += size) {
      chunks.push(columns.slice(i, i + size));
   }
   return chunks;
}

function layoutForChunk(pageWidth: number, bfeCount: number, showFields: boolean): TableLayout {
   const inner = tableInnerWidth(pageWidth);
   let fieldsW = showFields ? MIN_FIELDS_W : 0;
   const statusW = showFields ? STATUS_W : 0;
   const leftover = inner - PRIORITY_W - statusW - fieldsW;
   let bfeW = bfeCount ? Math.min(MAX_BFE_W, Math.max(MIN_BFE_W, leftover * 0.32 / bfeCount)) : 0;
   let identityW = leftover - bfeW * bfeCount;
   if (bfeCount && identityW < MIN_IDENTITY_W) {
      identityW = MIN_IDENTITY_W;
      bfeW = Math.max(4.4, (leftover - identityW) / bfeCount);
   }
   if (showFields && identityW > MAX_IDENTITY_W) {
      fieldsW += identityW - MAX_IDENTITY_W;
      identityW = MAX_IDENTITY_W;
   }
   return {
      priorityW: PRIORITY_W,
      identityW,
      statusW,
      fieldsW,
      bfeW,
      showFields,
   };
}

type DrawnLine = { kind: "label" | "body"; text: string };

function wrapLines(doc: jsPDF, text: string, width: number): string[] {
   return doc.splitTextToSize(text || "–", Math.max(width, 8));
}

function fieldLines(doc: jsPDF, fields: ServitutPdfField[], width: number): DrawnLine[] {
   const lines: DrawnLine[] = [];
   fields.forEach((field, index) => {
      if (index > 0) {
         lines.push({ kind: "label", text: "" });
      }
      lines.push({ kind: "label", text: field.label });
      wrapLines(doc, field.text, width).forEach((text) => lines.push({ kind: "body", text }));
   });
   return lines;
}

function takeLines(lines: DrawnLine[], start: number, maxHeight: number): { end: number; height: number } {
   let height = 0;
   let end = start;
   while (end < lines.length) {
      const line = lines[end];
      const h = line.kind === "label" ? (line.text ? LABEL_LINE_H : 1.2) : BODY_LINE_H;
      if (end > start && height + h > maxHeight) {
         break;
      }
      height += h;
      end += 1;
   }
   return { end, height };
}

function rowFill(tone: ServitutPdfRowModel["statusTone"]): readonly [number, number, number] {
   if (tone === "approved") {
      return COLORS.approved;
   }
   if (tone === "rejected") {
      return COLORS.rejected;
   }
   return COLORS.row;
}

function setRgb(doc: jsPDF, method: "setFillColor" | "setDrawColor" | "setTextColor", rgb: readonly [number, number, number]) {
   doc[method](rgb[0], rgb[1], rgb[2]);
}

function drawFooter(doc: jsPDF, model: ServitutPdfModel, pageWidth: number, pageHeight: number, page: number, total: number) {
   const y = pageHeight - 6;
   setRgb(doc, "setTextColor", COLORS.muted);
   doc.setFont("helvetica", "normal");
   doc.setFontSize(8);
   doc.text(model.generatedAt, MARGIN, y);
   doc.text(`Side ${page} af ${total}`, pageWidth - MARGIN, y - 3.2, { align: "right" });
   doc.text(model.generatedStamp, pageWidth - MARGIN, y, { align: "right" });
}

function drawTitle(
   doc: jsPDF,
   model: ServitutPdfModel,
   pageWidth: number,
   compact: boolean,
): number {
   let y = MARGIN + 4;
   setRgb(doc, "setTextColor", COLORS.headerText);
   doc.setFont("helvetica", "bold");
   doc.setFontSize(compact ? 11 : 16);
   doc.text(model.title, pageWidth / 2, y, { align: "center" });
   y += compact ? 5 : 7;
   doc.setFont("helvetica", "normal");
   doc.setFontSize(8);
   setRgb(doc, "setTextColor", COLORS.muted);
   doc.text(model.easementCountLabel, pageWidth / 2, y, { align: "center" });
   y += 5;
   if (!compact && model.caseDescription) {
      doc.setFont("helvetica", "bold");
      setRgb(doc, "setTextColor", COLORS.headerText);
      doc.text(`${Localization.getText("Problemstilling")}:`, MARGIN, y);
      y += 4;
      doc.setFont("helvetica", "normal");
      setRgb(doc, "setTextColor", COLORS.text);
      const lines = wrapLines(doc, model.caseDescription, tableInnerWidth(pageWidth));
      lines.forEach((line) => {
         doc.text(line, MARGIN, y);
         y += BODY_LINE_H;
      });
      y += 2;
   }
   return y + 1;
}

function tableHeaderHeight(columns: ServitutPdfColumnModel[]): number {
   const longest = columns.reduce((max, column) => Math.max(max, column.label.length), 0);
   return columns.length ? Math.min(36, Math.max(16, longest * 1.7 + 8)) : 9;
}

function withClip(doc: jsPDF, x: number, y: number, width: number, height: number, paint: () => void) {
   doc.saveGraphicsState();
   doc.rect(x, y, width, height, null);
   doc.clip();
   doc.discardPath();
   paint();
   doc.restoreGraphicsState();
}

function drawTableHeader(
   doc: jsPDF,
   model: ServitutPdfModel,
   layout: TableLayout,
   columns: ServitutPdfColumnModel[],
   y: number,
): number {
   const height = tableHeaderHeight(columns);
   const headers: Array<{ text: string; width: number; rotate?: boolean }> = [
      { text: Localization.getText("Priority"), width: layout.priorityW },
      { text: Localization.getText("Date / serial no."), width: layout.identityW },
   ];
   if (layout.showFields) {
      headers.push({ text: Localization.getText("Status"), width: layout.statusW });
      headers.push({ text: model.fieldHeader, width: layout.fieldsW });
   }
   columns.forEach((column) => headers.push({ text: column.label, width: layout.bfeW, rotate: true }));

   let x = MARGIN;
   headers.forEach((header) => {
      setRgb(doc, "setFillColor", COLORS.headerBg);
      setRgb(doc, "setDrawColor", COLORS.border);
      doc.setLineWidth(0.2);
      doc.rect(x, y, header.width, height, "FD");
      setRgb(doc, "setTextColor", COLORS.headerText);
      doc.setFont("helvetica", "bold");
      withClip(doc, x, y, header.width, height, () => {
         if (header.rotate) {
            doc.setFontSize(7);
            doc.text(header.text, x + header.width / 2 + 1.1, y + height - 2.2, { angle: 90 });
         } else if (header.width <= 20) {
            doc.setFontSize(7);
            const wrapped = wrapLines(doc, header.text, header.width - 2);
            wrapped.slice(0, 2).forEach((line, index) => {
               doc.text(line, x + header.width / 2, y + height / 2 - (wrapped.length - 1) * 1.5 + index * 3.1, { align: "center" });
            });
         } else {
            doc.setFontSize(header.width > 50 ? 7 : 8);
            const wrapped = wrapLines(doc, header.text, header.width - 3);
            wrapped.slice(0, 3).forEach((line, index) => {
               doc.text(line, x + 1.4, y + 4 + index * 3.1);
            });
         }
      });
      x += header.width;
   });
   return y + height;
}

function drawIdentity(
   doc: jsPDF,
   row: ServitutPdfRowModel,
   x: number,
   y: number,
   width: number,
) {
   const identityLines = wrapLines(doc, row.identity, width - CELL_PAD * 2);
   const headingLines = row.heading ? wrapLines(doc, row.heading, width - CELL_PAD * 2) : [];
   setRgb(doc, "setTextColor", COLORS.headerText);
   doc.setFont("helvetica", "bold");
   doc.setFontSize(8);
   let cursor = y + CELL_PAD + 2.8;
   identityLines.forEach((line) => {
      doc.text(line, x + CELL_PAD, cursor);
      cursor += BODY_LINE_H;
   });
   if (headingLines.length) {
      setRgb(doc, "setTextColor", COLORS.text);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      cursor += 0.6;
      headingLines.forEach((line) => {
         doc.text(line, x + CELL_PAD, cursor);
         cursor += BODY_LINE_H;
      });
   }
}

function identityHeight(doc: jsPDF, row: ServitutPdfRowModel, width: number): number {
   const identityLines = wrapLines(doc, row.identity, width - CELL_PAD * 2);
   const headingLines = row.heading ? wrapLines(doc, row.heading, width - CELL_PAD * 2) : [];
   return CELL_PAD * 2 + identityLines.length * BODY_LINE_H + (headingLines.length ? headingLines.length * BODY_LINE_H + 0.6 : 0) + 1.4;
}

function drawFields(
   doc: jsPDF,
   lines: DrawnLine[],
   x: number,
   y: number,
) {
   let cursor = y + CELL_PAD + 2.6;
   lines.forEach((line) => {
      if (line.kind === "label") {
         if (!line.text) {
            cursor += 1.2;
            return;
         }
         doc.setFont("helvetica", "bold");
         doc.setFontSize(8);
         setRgb(doc, "setTextColor", COLORS.headerText);
         doc.text(line.text, x + CELL_PAD, cursor);
         cursor += LABEL_LINE_H;
         return;
      }
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      setRgb(doc, "setTextColor", COLORS.text);
      doc.text(line.text, x + CELL_PAD, cursor);
      cursor += BODY_LINE_H;
   });
}

function drawIncludedMark(doc: jsPDF, x: number, y: number, width: number, height: number) {
   const radius = Math.min(2.6, width / 2 - 0.6, height / 2 - 1);
   setRgb(doc, "setFillColor", COLORS.includedBg);
   doc.circle(x + width / 2, y + height / 2, radius, "F");
   setRgb(doc, "setTextColor", COLORS.includedText);
   doc.setFont("helvetica", "bold");
   doc.setFontSize(7);
   doc.text("x", x + width / 2, y + height / 2 + 1, { align: "center" });
}

function drawRowFragment(
   doc: jsPDF,
   row: ServitutPdfRowModel,
   layout: TableLayout,
   columns: ServitutPdfColumnModel[],
   fieldSlice: DrawnLine[],
   y: number,
   height: number,
   continued = false,
) {
   const fill = rowFill(row.statusTone);
   const widths = [
      layout.priorityW,
      layout.identityW,
      ...(layout.showFields ? [layout.statusW, layout.fieldsW] : []),
      ...columns.map(() => layout.bfeW),
   ];
   let x = MARGIN;
   widths.forEach((width) => {
      setRgb(doc, "setFillColor", fill);
      setRgb(doc, "setDrawColor", COLORS.border);
      doc.setLineWidth(0.2);
      doc.rect(x, y, width, height, "FD");
      x += width;
   });
   const accent = row.statusTone === "approved"
      ? COLORS.accentApproved
      : row.statusTone === "rejected"
         ? COLORS.accentRejected
         : null;
   if (accent) {
      setRgb(doc, "setFillColor", accent);
      doc.rect(MARGIN, y, 1.3, height, "F");
   }

   let cursorX = MARGIN + layout.priorityW + layout.identityW;
   if (!continued) {
      setRgb(doc, "setTextColor", COLORS.headerText);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      withClip(doc, MARGIN, y, layout.priorityW, height, () => {
         doc.text(row.priority, MARGIN + layout.priorityW / 2, y + Math.min(7.5, height / 2 + 1.5), { align: "center" });
      });

      withClip(doc, MARGIN + layout.priorityW, y, layout.identityW, height, () => {
         drawIdentity(doc, row, MARGIN + layout.priorityW, y, layout.identityW);
      });

      if (layout.showFields) {
         withClip(doc, cursorX, y, layout.statusW, height, () => {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(7.5);
            setRgb(doc, "setTextColor", COLORS.text);
            const statusLines = wrapLines(doc, row.status, layout.statusW - CELL_PAD * 2);
            statusLines.forEach((line, index) => {
               doc.text(line, cursorX + CELL_PAD, y + CELL_PAD + 3.2 + index * BODY_LINE_H);
            });
         });
      }
   }

   if (layout.showFields) {
      cursorX += layout.statusW;
      withClip(doc, cursorX, y, layout.fieldsW, height, () => {
         drawFields(doc, fieldSlice, cursorX, y);
      });
      cursorX += layout.fieldsW;
   }

   if (!continued) {
      columns.forEach((column) => {
         if (row.includedBfes.includes(column.bfe)) {
            drawIncludedMark(doc, cursorX, y, layout.bfeW, height);
         }
         cursorX += layout.bfeW;
      });
   }
}

export async function buildServitutPdfBlob(
   rows: ServitutRow[],
   columns: number[],
   columnLabels: Record<string, string>,
   options?: {
      caseDescription?: string;
      columnDetails?: Record<string, ServitutMatrixColumnDetail>;
      onProgress?: (state: ServitutExportProgress) => void | Promise<void>;
   },
): Promise<Blob> {
   const model = buildServitutPdfModel(rows, columns, columnLabels, options);
   const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
   const pageWidth = doc.internal.pageSize.getWidth();
   const pageHeight = doc.internal.pageSize.getHeight();
   const pageBottom = pageHeight - FOOTER_H;
   const bfeChunks = chunkPdfBfeColumns(model.columns, pageWidth);
   const totalSteps = 1 + model.rows.length * bfeChunks.length;
   let completedSteps = 0;
   const emit = async (progress: Partial<ServitutExportProgress> & { stage?: ServitutExportProgress["stage"] }) => {
      await options?.onProgress?.({
         completedSteps,
         totalSteps,
         stage: "pdf",
         activeRowKey: null,
         ...progress,
      });
   };
   await emit({ completedSteps: 0 });

   let firstPage = true;
   for (let chunkIndex = 0; chunkIndex < bfeChunks.length; chunkIndex += 1) {
      const bfeChunk = bfeChunks[chunkIndex];
      const showFields = chunkIndex === 0;
      const layout = layoutForChunk(pageWidth, bfeChunk.length, showFields);
      if (!firstPage) {
         doc.addPage();
      }
      firstPage = false;
      let y = drawTableHeader(doc, model, layout, bfeChunk, drawTitle(doc, model, pageWidth, chunkIndex > 0));

      for (let rowIndex = 0; rowIndex < model.rows.length; rowIndex += 1) {
         const row = model.rows[rowIndex];
         await emit({
            activeRowKey: row.id,
            documentIndex: rowIndex + 1,
            documentTotal: model.rows.length,
         });
         const idH = identityHeight(doc, row, layout.identityW);
         const allFieldLines = showFields ? fieldLines(doc, row.fields, layout.fieldsW - CELL_PAD * 2) : [];
         let fieldStart = 0;
         do {
            if (y + MIN_ROW_H > pageBottom) {
               doc.addPage();
               y = drawTableHeader(doc, model, layout, bfeChunk, drawTitle(doc, model, pageWidth, true));
            }
            const available = Math.max(MIN_ROW_H, pageBottom - y - 0.4);
            const taken = allFieldLines.length
               ? takeLines(allFieldLines, fieldStart, Math.max(BODY_LINE_H, available - CELL_PAD * 2))
               : { end: fieldStart + 1, height: 0 };
            const slice = allFieldLines.slice(fieldStart, taken.end);
            const identityPart = fieldStart === 0 ? Math.min(idH, available) : 0;
            const height = Math.min(
               available,
               Math.max(MIN_ROW_H, identityPart, taken.height + CELL_PAD * 2),
            );
            drawRowFragment(doc, row, layout, bfeChunk, slice, y, height, fieldStart > 0);
            y += height;
            fieldStart = allFieldLines.length ? taken.end : 1;
         } while (allFieldLines.length > 0 && fieldStart < allFieldLines.length);
         completedSteps += 1;
         await emit({
            activeRowKey: row.id,
            documentIndex: rowIndex + 1,
            documentTotal: model.rows.length,
         });
      }
   }

   const totalPages = doc.getNumberOfPages();
   for (let page = 1; page <= totalPages; page += 1) {
      doc.setPage(page);
      drawFooter(doc, model, pageWidth, pageHeight, page, totalPages);
   }
   completedSteps = totalSteps;
   await emit({ completedSteps, activeRowKey: null });
   return doc.output("blob");
}
